import { FIVE_AXIS } from '../constants/machine-state/kinematics-mode';
import * as messages from '../gcode/interpreter-messages';
import { EPS } from '../constants';
import { Matrix4, Vector4 } from 'three';
//import produce from 'immer';

import Dmu95 from '../components/viewer3d/Dmu95';

export const isSuperSecret = true;

export const safeJoints = [ 6, 3, 0, 0, 0 ];

export const PIVOTX = 20.0788;
export const PIVOTY = -13.9754;
export const PIVOTZ = -27.5361;

export const toolLength = (toolLengthOffset) => {
  return toolLengthOffset;
};

export const DZ = -70/25.4;
export const DY = -69.95/25.4;

export const computeDynamicWorkOffsets = (workOffsets, position) => {
  const atZero = inverseFiveAxis(workOffsets, 0);
  const atCurrentOrientation = inverseFiveAxis({ X: atZero[0], Y: atZero[1], Z: atZero[2], A: position.A, B: position.B, C: position.C }, 0);
  return {
    X: atCurrentOrientation[0],
    Y: atCurrentOrientation[1],
    Z: atCurrentOrientation[2],
    A: 0,
    B: 0,
    C: 0
  };
};

export const orientationFromToolDirection = (x,y,z) => {
  const A = Math.acos(z)*180/Math.PI;
  let C;

  if(A < .00001) {
    C = 0;
  } else {
    C = Math.atan2(y, x)*180/Math.PI;

    if(C < 0) {
      C += 360;
    }

    C = 180-C;
  }

  return [ A, C ];
};

export const toolDirection = (joints) => {
  const A = joints[3];
  const C = joints[4];

  const Arad = A*Math.PI/180;
  const Crad = C*Math.PI/180;

  const CA = Math.cos(Arad);
  const SA = Math.sin(Arad);
  const CC = Math.cos(Crad);
  const SC = Math.sin(Crad);

  return { X: SC*SA, Y: CC*SA, Z: CA }; 
};

export const workPieceSpace = (joints, toolLengthOffset) => {
//  return forwardFiveAxis(produce(joints, (draft) => draft[2] = draft[2]+.004), toolLengthOffset);
  return forwardFiveAxis(joints, toolLengthOffset);
};

const translateOrigin = (new Matrix4()).makeTranslation(PIVOTX, PIVOTY, PIVOTZ);
const translateOriginInverse = (new Matrix4()).makeTranslation(-PIVOTX, -PIVOTY, -PIVOTZ);
const translateLocalOrigin = (new Matrix4()).makeTranslation(-PIVOTX, -PIVOTY-DY, -PIVOTZ-DZ);
const translateLocalOriginInverse = (new Matrix4()).makeTranslation(PIVOTX, PIVOTY+DY, PIVOTZ+DZ);
const translateDelta = (new Matrix4()).makeTranslation(0,DY,DZ);
const translateDeltaInverse = (new Matrix4()).makeTranslation(0,-DY,-DZ);

const rotateA = (new Matrix4()).makeRotationX(0);
const rotateC = (new Matrix4()).makeRotationZ(0);

const forwardMatrix = new Matrix4();
const inverseMatrix = new Matrix4();
const jointsVector = new Vector4();
const axesVector = new Vector4();

export const forwardFiveAxis = (joints, toolLengthOffset) => {
  const A = joints[3];
  const C = joints[4];

  const Arad = A*Math.PI/180;
  const Crad = C*Math.PI/180;

  rotateA.makeRotationX(-Arad);
  rotateC.makeRotationZ(-Crad);

  forwardMatrix.copy(translateLocalOriginInverse);
  forwardMatrix.multiply(rotateC);
  forwardMatrix.multiply(translateDeltaInverse);
  forwardMatrix.multiply(rotateA);
  forwardMatrix.multiply(translateOriginInverse);

  axesVector.set(joints[0], joints[1], joints[2]-toolLengthOffset, 1)
  axesVector.applyMatrix4(forwardMatrix);

  return { 
    X: axesVector.x, 
    Y: axesVector.y, 
    Z: axesVector.z, 
    A: A,
    B: 0, 
    C: C 
  };
};

export const inverseFiveAxis = (position, toolLengthOffset) => {
  const A = position.A;
  const C = position.C;

  const Arad = A*Math.PI/180;
  const Crad = C*Math.PI/180;

  rotateA.makeRotationX(Arad);
  rotateC.makeRotationZ(Crad);

  inverseMatrix.copy(translateOrigin);
  inverseMatrix.multiply(rotateA);
  inverseMatrix.multiply(translateDelta);
  inverseMatrix.multiply(rotateC);
  inverseMatrix.multiply(translateLocalOrigin);

  jointsVector.set(position.X, position.Y, position.Z, 1);
  jointsVector.applyMatrix4(inverseMatrix);

  return [ jointsVector.x, jointsVector.y, jointsVector.z+toolLengthOffset, A, C ];
};

export const kinematics = {
  inverseKinematics: (machineState, machineSpace) => {
    const kinematicsMode = machineState.kinematicsMode;

    let toolLengthOffset = 0;

    if(!machineSpace) {
      toolLengthOffset = machineState.tool.toolLengthOffset.Z;
    }

    const position = machineState.motion.position;

    if(kinematicsMode === FIVE_AXIS) {
      return inverseFiveAxis(position, toolLengthOffset);
    } else {
      return [ position.X, position.Y, position.Z+toolLengthOffset, position.A, position.C ];
    }
  },
  forwardKinematics: (machineState, machineSpace) => {
    const kinematicsMode = machineState.kinematicsMode;
    const toolLengthOffset = machineSpace ? 0 : machineState.tool.toolLengthOffset.Z;
    const joints = machineState.joints;

    if(kinematicsMode === FIVE_AXIS) {
      return forwardFiveAxis(joints, toolLengthOffset);
    } else {
      return { 
        X: joints[0], 
        Y: joints[1], 
        Z: joints[2]-toolLengthOffset, 
        A: joints[3], 
        B: 0,
        C: joints[4] 
      };
    }
  }
};

export const limits = {
  extents: {
    max: [ 37.4088, .002, .002, 120.02, 999999 ],
    min: [ -0.002, -33.4793, -25.6, -120.02, -999999]
  },
  // x,y,z in inches/second
  // a,b in degrees/second
  velocities: [ 25, 25, 25, 90, 90 ],
  accelerations: [ 289, 289, 289, 1040, 1040 ]
};

export const jointLabels = [ "X", "Y", "Z", "A", "C" ];

export const getLimitErrors = (joints, limits) => {
  const errors = [];
  for(var i = 0; i < joints.length; i++) {
    const joint = joints[i];
    if(joint < limits.extents.min[i]-EPS) {
      // suggestion for z
      // suggestion: "lengthen tool by " + Math.abs(joint-limits.extents.min[i])
      errors.push(messages.MinimumLimitError(jointLabels[i], Math.abs(joint-limits.extents.min[i])));
    } else if(joint > limits.extents.max[i]+EPS) {
      // suggestsion for z
      // suggestion: "shorten tool by " + Math.abs(joint-limits.extends.max[i])
      errors.push(messages.MaximumLimitError(jointLabels[i], Math.abs(joint-limits.extents.max[i])));
    }
  }

  return errors.length > 0 ? errors : [];
};

export const name = "DMU 95";
export const Machine = Dmu95;
